/**
 * CreerRolePage
 * Formulaire de création d'un nouveau rôle
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { useCreerRole, usePermissions } from '../../infrastructure/api/useRoles';
import type { CreerRoleData, PermissionDTO } from '../../application/dto/dtos';
import { grouperPermissionsParRessource } from '../store/rolesStore';

const NIVEAUX = [
  { valeur: 1, label: 'Niveau 1 — Super Administrateur' },
  { valeur: 2, label: 'Niveau 2 — Admin Caritas' },
  { valeur: 3, label: 'Niveau 3 — Admin National' },
  { valeur: 4, label: 'Niveau 4 — Admin Diocésain' },
  { valeur: 5, label: 'Niveau 5 — Gestionnaire' },
  { valeur: 6, label: 'Niveau 6 — Analyste' },
  { valeur: 7, label: 'Niveau 7 — Observateur' },
];

export function CreerRolePage() {
  const navigate = useNavigate();
  const creerRole = useCreerRole();
  const { data: permissions, isLoading: permissionsLoading } = usePermissions();

  const [formData, setFormData] = useState<CreerRoleData>({
    nom: '',
    slug: '',
    description: '',
    niveau: 7,
    isActive: true,
  });
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Générer automatiquement le slug depuis le nom
  const handleNomChange = (nom: string) => {
    const slug = nom
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    setFormData((prev) => ({ ...prev, nom, slug }));
  };

  const togglePermission = (idPermission: string) => {
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(idPermission)) {
        next.delete(idPermission);
      } else {
        next.add(idPermission);
      }
      return next;
    });
  };

  const toggleRessource = (permsRessource: PermissionDTO[]) => {
    const tous = permsRessource.every((p) => selectedPermissions.has(p.idPermission));
    setSelectedPermissions((prev) => {
      const next = new Set(prev);
      for (const p of permsRessource) {
        if (tous) {
          next.delete(p.idPermission);
        } else {
          next.add(p.idPermission);
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.nom.trim()) {
      setError('Le nom est obligatoire');
      return;
    }
    if (!formData.slug.trim()) {
      setError('Le slug est obligatoire');
      return;
    }

    try {
      await creerRole.mutateAsync({
        ...formData,
        // Les permissions seront assignées séparément (API assign-permissions)
      });
      setSuccess(true);
      setTimeout(() => navigate('/admin/roles'), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la création');
    }
  };

  const permissionsGroupees = grouperPermissionsParRessource(permissions ?? []);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/roles')}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300
                   rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Créer un rôle
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Définissez les attributs et les permissions du nouveau rôle
          </p>
        </div>
      </div>

      {/* Message de succès */}
      {success && (
        <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-900/20 border
                       border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          Rôle créé avec succès ! Redirection...
        </div>
      )}

      {/* Message d'erreur */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 border
                       border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Informations générales */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200
                       dark:border-gray-700 p-6 space-y-4">
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Informations générales
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Nom *
              </label>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => handleNomChange(e.target.value)}
                placeholder="ex: Coordinateur Régional"
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Slug *
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                placeholder="ex: coordinateur-regional"
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono
                         focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Identifiant unique, généré automatiquement depuis le nom
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Niveau hiérarchique *
            </label>
            <select
              value={formData.niveau}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, niveau: parseInt(e.target.value, 10) }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {NIVEAUX.map((n) => (
                <option key={n.valeur} value={n.valeur}>
                  {n.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              placeholder="Décrivez le rôle et ses responsabilités..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700 dark:text-gray-300">
              Rôle actif (visible et assignable aux utilisateurs)
            </label>
          </div>
        </div>

        {/* Permissions */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200
                       dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              Permissions ({selectedPermissions.size} sélectionnée{selectedPermissions.size > 1 ? 's' : ''})
            </h2>
            {permissionsLoading && (
              <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            )}
          </div>

          {permissionsGroupees.length === 0 && !permissionsLoading ? (
            <p className="text-sm text-gray-500 italic">Aucune permission disponible</p>
          ) : (
            <div className="space-y-6">
              {permissionsGroupees.map(({ ressource, permissions: perms }) => {
                const toutesSelectionnees = perms.every((p) =>
                  selectedPermissions.has(p.idPermission),
                );
                const certainesSelectionnees = perms.some((p) =>
                  selectedPermissions.has(p.idPermission),
                );

                return (
                  <div key={ressource}>
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        checked={toutesSelectionnees}
                        ref={(el) => {
                          if (el) el.indeterminate = !toutesSelectionnees && certainesSelectionnees;
                        }}
                        onChange={() => toggleRessource(perms)}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                      />
                      <span className="font-medium text-gray-800 dark:text-gray-200 capitalize">
                        {ressource}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        ({perms.length} action{perms.length > 1 ? 's' : ''})
                      </span>
                    </div>
                    <div className="ml-6 grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {perms.map((perm) => (
                        <label
                          key={perm.idPermission}
                          className="flex items-center gap-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedPermissions.has(perm.idPermission)}
                            onChange={() => togglePermission(perm.idPermission)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {perm.action}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            type="button"
            onClick={() => navigate('/admin/roles')}
            disabled={creerRole.isLoading}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600
                     text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50
                     dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={creerRole.isLoading || success}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700
                     transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {creerRole.isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Création...
              </>
            ) : (
              'Créer le rôle'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
